import { Box, Button } from '@factorialco/deprecated-design-system'
import { AtsRejectionReasonDecisionMakersEnum } from 'generated/resources/datatable.graphql'

import Modal from 'modules/core/components/Modal'
import Select from 'modules/core/components/Select'
import TextField from 'modules/core/components/TextField'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { FormErrors, useForm } from 'modules/core/hooks/useForm'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { useCreateRejectionReason } from 'modules/ats/hooks/rejectionReasons/useCreateRejectionReasons'

interface Props {
  onClose: () => void
}

interface AddRejectionReasonFormValues {
  reason: string
  companyId: number
  decisionMaker: string
}

export const AddRejectionReasonModal = ({ onClose }: Props) => {
  const { company } = useCurrentSession()
  const { loading, create } = useCreateRejectionReason()

  const form = useForm<AddRejectionReasonFormValues>({
    defaultValues: {
      reason: '',
      decisionMaker: '',
      companyId: company.id,
    },
    validate(data) {
      const errors: FormErrors<AddRejectionReasonFormValues> = {}

      if (!data.reason) {
        errors.reason = i18n.t('common.empty')
      }
      if (!data.decisionMaker) {
        errors.decisionMaker = i18n.t('common.select_default')
      }

      return errors
    },
    async onSubmit(data, { setErrors }) {
      const result = await create({
        ...data,
        reason: data.reason,
        decisionMaker: data.decisionMaker as AtsRejectionReasonDecisionMakersEnum,
        companyId: company.id,
      })

      const handled = handleResult(result, ({ reason, decisionMaker }) => {
        setErrors({
          reason,
          decisionMaker,
        })
      })

      if (handled.success) {
        onClose()
      }

      setFlashMessage(handled.success)
      return handled
    },
  })

  const setFlashMessage = (success: boolean) => {
    const type = success ? 'correct' : 'wrong'
    const message = success
      ? i18n.t('ats.rejection_reasons.settings.save.success')
      : i18n.t('ats.rejection_reasons.settings.save.error')

    app.setFlash({
      type: type,
      message: message,
      time: 2000,
    })
  }

  const decisionMakerOptions = [
    {
      label: i18n.t('ats.rejection_reasons.settings.customize_reason.decision_maker.company'),
      value: 'company',
    },
    {
      label: i18n.t('ats.rejection_reasons.settings.customize_reason.decision_maker.candidate'),
      value: 'candidate',
    },
  ]

  return (
    <Modal
      illustration="document-edit"
      title={i18n.t('ats.rejection_reasons.settings.customize_reason.title')}
      description={i18n.t('ats.rejection_reasons.settings.customize_reason.description')}
      onSubmit={() => form.handleSubmit()}
      onClose={onClose}
      footer={
        <>
          <Button type="secondary" onClick={onClose}>
            {i18n.t('common.cancel')}
          </Button>
          <Button disabled={form.isSubmitting || loading} submit>
            {i18n.t('ats.rejection_reasons.settings.customize_reason.save')}
          </Button>
        </>
      }
    >
      <Box gap={'s8'}>
        <Select
          mandatory
          none={i18n.t('common.select')}
          label={i18n.t('ats.rejection_reasons.settings.customize_reason.decision_maker.label')}
          options={decisionMakerOptions}
          value={form.values.decisionMaker}
          error={form.touched.decisionMaker ? form.errors.decisionMaker : undefined}
          onChange={(decisionMaker) =>
            form.setValues({ ...form.values, decisionMaker: decisionMaker })
          }
        />
        <TextField
          label={i18n.t('ats.rejection_reasons.settings.customize_reason.reason')}
          placeholder={i18n.t('ats.rejection_reasons.settings.customize_reason.placeholder')}
          mandatory
          onChange={(reason) => form.setValues({ ...form.values, reason: reason })}
          error={form.touched.reason ? form.errors.reason : undefined}
        />
      </Box>
    </Modal>
  )
}
